/**
 * Video URL parsing and thumbnail utilities for YouTube and Vimeo
 */

// YouTube URL patterns
const YOUTUBE_REGEX = /(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;

// Vimeo URL patterns
const VIMEO_REGEX = /(?:vimeo\.com\/)(\d+)/;

/**
 * Extract video info from a URL
 * Returns { platform, videoId, url } or null if not a recognized video URL
 */
export const parseVideoUrl = (url) => {
  if (!url || typeof url !== 'string') return null;

  const trimmed = url.trim();

  const youtubeMatch = trimmed.match(YOUTUBE_REGEX);
  if (youtubeMatch) {
    return {
      platform: 'youtube',
      videoId: youtubeMatch[1],
      url: trimmed
    };
  }

  const vimeoMatch = trimmed.match(VIMEO_REGEX);
  if (vimeoMatch) {
    return {
      platform: 'vimeo',
      videoId: vimeoMatch[1],
      url: trimmed
    };
  }

  return null;
};

/**
 * Get thumbnail URL for a video
 * YouTube: direct URL (no API call needed)
 * Vimeo: requires oEmbed fetch
 */
export const getVideoThumbnail = (platform, videoId) => {
  if (platform === 'youtube') {
    return `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
  }
  // Vimeo requires async fetch — handled separately
  return null;
};

/**
 * Fetch Vimeo thumbnail via oEmbed API
 * Returns thumbnail URL string or null on failure
 */
export const fetchVimeoThumbnail = async (videoUrl) => {
  try {
    const response = await fetch(
      `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(videoUrl)}&width=320`
    );
    if (!response.ok) return null;
    const data = await response.json();
    return data.thumbnail_url || null;
  } catch (error) {
    console.warn('Vimeo oEmbed fetch failed:', error.message);
    return null;
  }
};

/**
 * Check if a URL is a recognized video URL
 */
export const isVideoUrl = (url) => {
  return parseVideoUrl(url) !== null;
};

/**
 * Scan text for the first YouTube or Vimeo URL
 * Returns parsed video info or null
 */
export const detectVideoInText = (text) => {
  if (!text) return null;

  // Match URLs in text
  const urlRegex = /https?:\/\/[^\s<]+/g;
  const urls = text.match(urlRegex);

  if (!urls) return null;

  for (const url of urls) {
    const video = parseVideoUrl(url);
    if (video) return video;
  }

  return null;
};
